import React from "react";
import { useSelector } from "react-redux";

import { Button, CommonErrorModal, Header, Modal, Title, Breadcrumbs, Preloader, LoginModal } from "../../components";

import { feedbackSelector } from "../../redux/feedback/selectors";
import { contactsSelector } from "../../redux/contacts/selectors";
import { authAndRegSelector, isAuthenticatedSelector } from "../../redux/profile/authAndReg/selectors";
import { fetchContacts } from "../../redux/contacts/asyncActions";
import { setShowWindow } from "../../redux/profile/authAndReg/slice";
import { useAppDispatch } from "../../redux/store";
import { FeedbackForm } from "./Forms/FeedbackForm";
import { formatPhone } from "../../utils";

import styles from "./Contacts.module.scss";
import { ContactsPropsType } from "./types";
import { ModalTexts } from "../../resources/locale";
import { WindowNames } from "../../resources/constants";
import {MapDirection} from "../AboutPage/Components/Map/Map";
import { useLocation } from "react-router-dom";

export const Contacts: React.FC<ContactsPropsType> = ({ title }) => {
  const { status } = useSelector(feedbackSelector);
  const { contacts, isLoading } = useSelector(contactsSelector);
  const auth = useSelector(isAuthenticatedSelector);
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = React.useState<boolean>(false);

  const location = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  React.useEffect(() => {
    dispatch(fetchContacts());
  }, [dispatch]);

  React.useEffect(() => {
    if (auth && isLoginModalOpen) {
      setIsLoginModalOpen(false);
      setIsOpen(true);
    }
  }, [auth, isLoginModalOpen]);

  const confirmModal = () => {
    setIsOpen(false)
  }

  const handleAskQuestion = () => {
    if (!auth) {
      dispatch(setShowWindow(WindowNames.AUTH));
      setIsLoginModalOpen(true);
    } else {
      setIsOpen(true);
    }
  }

  return (
    <>
      <Header title={title} />
      <main>
        <div className="container">
          <Breadcrumbs />
        </div>
        <section className="container">
          <Title>{title}</Title>
          <div className={styles.wrapper}>
            {isLoading ? (
              <div className={styles.loader}>
                <Preloader />
              </div>
            ) : contacts ? (
              <div className={styles.info}>
                {contacts.phone_number && (
                  <div className={styles.info_item}>
                    <div className={styles.info_item_label}>Телефон</div>
                    <a href={`tel:${contacts.phone_number}`} className={`${styles.info_item_content} ` + `${styles.phone}`}>
                      {formatPhone(contacts.phone_number)}
                    </a>
                  </div>
                )}
                {contacts.address && (
                  <div className={styles.info_item}>
                    <div className={styles.info_item_label}>Адрес</div>
                    <a 
                      href="https://yandex.ru/maps/org/tennis_ru/1341065211/?ll=37.410421%2C55.570022&z=18.54" 
                      target="_blank" 
                      className={`${styles.info_item_content} ` + `${styles.address}`} 
                      rel="noreferrer"
                    >
                      {contacts.address}
                    </a>
                  </div>
                )}
                {contacts.email && (
                  <div className={styles.info_item}>
                    <div className={styles.info_item_label}>E-mail</div>
                    <a href={`mailto:${contacts.email}`} className={`${styles.info_item_content} ` + `${styles.email}`}>
                      {contacts.email}
                    </a>
                  </div>
                )}
                {contacts.website && (
                  <div className={styles.info_item}>
                    <div className={styles.info_item_label}>Адрес сайта</div>
                    <a 
                      href={contacts.website.startsWith('http') ? contacts.website : `https://${contacts.website}`} 
                      target="_blank" 
                      rel="noreferrer" 
                      className={`${styles.info_item_content} ` + `${styles.website}`}
                    >
                      {contacts.website}
                    </a>
                  </div>
                )}
                {(contacts.network_link_1 || contacts.network_link_2) && (
                  <div className={styles.social}>
                    {contacts.network_link_1 && (
                      <a 
                        href={contacts.network_link_1} 
                        target="_blank" 
                        rel="noreferrer" 
                        className={styles.social__link}
                        aria-label="Instagram"
                      >
                        <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M34.6039 1.83338H9.25272C8.26593 1.83217 7.28883 2.02801 6.37873 2.40943C5.46863 2.79084 4.64385 3.35014 3.95276 4.05453C3.26168 4.75892 2.7182 5.59422 2.35421 6.51142C1.99022 7.42863 1.81304 8.40929 1.83307 9.39588V14.4375C1.83307 14.6604 1.9216 14.8741 2.07918 15.0317C2.23676 15.1893 2.45049 15.2778 2.67335 15.2778H11.0761C11.299 15.2778 11.5127 15.1893 11.6703 15.0317C11.8279 14.8741 11.9164 14.6604 11.9164 14.4375C11.9164 14.2147 11.8279 14.001 11.6703 13.8434C11.5127 13.6858 11.299 13.5973 11.0761 13.5973H3.51363V9.39588C3.49606 8.63067 3.63146 7.86965 3.91186 7.15745C4.19227 6.44524 4.61205 5.79619 5.14659 5.24835C5.68112 4.70051 6.31966 4.2649 7.02475 3.96707C7.72985 3.66925 8.48731 3.51519 9.25272 3.51394H34.6039C36.1633 3.51571 37.6584 4.13599 38.7611 5.23868C39.8638 6.34138 40.4841 7.83644 40.4858 9.39588V13.5973H32.9233C32.7005 13.5973 32.4868 13.6858 32.3292 13.8434C32.1716 14.001 32.0831 14.2147 32.0831 14.4375C32.0831 14.6604 32.1716 14.8741 32.3292 15.0317C32.4868 15.1893 32.7005 15.2778 32.9233 15.2778H41.3261C41.549 15.2778 41.7627 15.1893 41.9203 15.0317C42.0779 14.8741 42.1664 14.6604 42.1664 14.4375V9.39588C42.1642 7.39086 41.3667 5.4686 39.9489 4.05084C38.5312 2.63308 36.6089 1.8356 34.6039 1.83338ZM41.3261 16.9584C41.1033 16.9584 40.8895 17.0469 40.732 17.2045C40.5744 17.3621 40.4858 17.5758 40.4858 17.7987V34.6042C40.4841 36.1637 39.8638 37.6587 38.7611 38.7614C37.6584 39.8641 36.1633 40.4844 34.6039 40.4862H9.39557C7.83613 40.4844 6.34107 39.8641 5.23837 38.7614C4.13568 37.6587 3.5154 36.1637 3.51363 34.6042V17.7987C3.51363 17.5758 3.4251 17.3621 3.26751 17.2045C3.10993 17.0469 2.8962 16.9584 2.67335 16.9584C2.45049 16.9584 2.23676 17.0469 2.07918 17.2045C1.9216 17.3621 1.83307 17.5758 1.83307 17.7987V34.6042C1.83529 36.6092 2.63277 38.5315 4.05053 39.9493C5.46829 41.367 7.39055 42.1645 9.39557 42.1667H34.6039C36.6089 42.1645 38.5312 41.367 39.9489 39.9493C41.3667 38.5315 42.1642 36.6092 42.1664 34.6042V17.7987C42.1664 17.5758 42.0779 17.3621 41.9203 17.2045C41.7627 17.0469 41.549 16.9584 41.3261 16.9584Z" fill="#00CC66"/>
                          <path d="M11.9165 23.6806C11.9165 25.6749 12.5079 27.6244 13.6159 29.2826C14.7238 30.9408 16.2986 32.2332 18.1411 32.9964C19.9836 33.7596 22.011 33.9593 23.967 33.5702C25.923 33.1811 27.7197 32.2208 29.1298 30.8106C30.54 29.4004 31.5004 27.6038 31.8894 25.6478C32.2785 23.6918 32.0788 21.6644 31.3156 19.8219C30.5524 17.9794 29.26 16.4046 27.6018 15.2966C25.9436 14.1887 23.9941 13.5973 21.9998 13.5973C19.3265 13.6004 16.7636 14.6638 14.8733 16.5541C12.983 18.4444 11.9196 21.0073 11.9165 23.6806ZM30.4026 23.6806C30.4026 25.3425 29.9098 26.9671 28.9865 28.349C28.0632 29.7308 26.7508 30.8078 25.2154 31.4438C23.68 32.0798 21.9905 32.2462 20.3605 31.9219C18.7306 31.5977 17.2333 30.7974 16.0582 29.6223C14.883 28.4471 14.0827 26.9499 13.7585 25.3199C13.4343 23.6899 13.6007 22.0004 14.2367 20.465C14.8727 18.9296 15.9497 17.6173 17.3315 16.694C18.7133 15.7707 20.3379 15.2778 21.9998 15.2778C24.2276 15.2803 26.3635 16.1664 27.9388 17.7417C29.5141 19.317 30.4002 21.4528 30.4026 23.6806Z" fill="#00CC66"/>
                          <path d="M15.2778 23.6806C15.2778 25.0102 15.6721 26.3098 16.4107 27.4153C17.1494 28.5208 18.1992 29.3824 19.4276 29.8912C20.6559 30.3999 22.0075 30.5331 23.3115 30.2737C24.6155 30.0143 25.8133 29.3741 26.7534 28.434C27.6935 27.4938 28.3337 26.2961 28.5931 24.9921C28.8525 23.6881 28.7194 22.3365 28.2106 21.1081C27.7018 19.8798 26.8402 18.8299 25.7347 18.0913C24.6293 17.3527 23.3296 16.9584 22.0001 16.9584C20.2178 16.9604 18.5092 17.6693 17.2489 18.9295C15.9887 20.1897 15.2798 21.8984 15.2778 23.6806ZM27.0417 23.6806C27.0417 24.6778 26.746 25.6525 26.192 26.4816C25.6381 27.3107 24.8507 27.9569 23.9294 28.3385C23.0082 28.7201 21.9945 28.82 21.0165 28.6254C20.0385 28.4309 19.1401 27.9507 18.4351 27.2456C17.73 26.5405 17.2498 25.6422 17.0553 24.6642C16.8607 23.6862 16.9606 22.6725 17.3422 21.7513C17.7238 20.83 18.37 20.0426 19.1991 19.4886C20.0282 18.9346 21.0029 18.639 22.0001 18.639C23.3367 18.6405 24.6182 19.1722 25.5633 20.1173C26.5085 21.0625 27.0402 22.344 27.0417 23.6806ZM31.2431 11.0765C31.7417 11.0765 32.2291 10.9286 32.6436 10.6516C33.0582 10.3746 33.3813 9.98093 33.5721 9.52031C33.7629 9.05968 33.8128 8.55283 33.7155 8.06383C33.6182 7.57484 33.3782 7.12567 33.0256 6.77313C32.6731 6.42058 32.2239 6.1805 31.7349 6.08323C31.2459 5.98596 30.7391 6.03588 30.2784 6.22668C29.8178 6.41747 29.4241 6.74058 29.1471 7.15512C28.8701 7.56967 28.7223 8.05705 28.7223 8.55562C28.7223 9.22419 28.9879 9.86538 29.4606 10.3381C29.9334 10.8109 30.5745 11.0765 31.2431 11.0765ZM31.2431 7.71535C31.4093 7.71535 31.5718 7.76463 31.7099 7.85696C31.8481 7.94929 31.9558 8.08052 32.0194 8.23406C32.083 8.38761 32.0997 8.55656 32.0672 8.71955C32.0348 8.88255 31.9548 9.03228 31.8373 9.14979C31.7198 9.26731 31.57 9.34733 31.407 9.37976C31.244 9.41218 31.0751 9.39554 30.9216 9.33194C30.768 9.26834 30.6368 9.16064 30.5444 9.02246C30.4521 8.88428 30.4028 8.72182 30.4028 8.55562C30.4028 8.33277 30.4914 8.11904 30.6489 7.96146C30.8065 7.80388 31.0203 7.71535 31.2431 7.71535Z" fill="#00CC66"/>
                        </svg>
                      </a>
                    )}
                    {contacts.network_link_2 && (
                      <a 
                        href={contacts.network_link_2} 
                        target="_blank" 
                        rel="noreferrer" 
                        className={styles.social__link}
                        aria-label="Facebook"
                      >
                        <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20.6557 42.1667H5.86683C3.63505 42.1667 1.8335 40.3652 1.8335 38.1334V5.86671C1.8335 3.63493 3.63505 1.83337 5.86683 1.83337H38.1335C40.3653 1.83337 42.1668 3.63493 42.1668 5.86671V38.1334C42.1668 40.3652 40.3653 42.1667 38.1335 42.1667H28.7224V27.3778H32.7557L35.4446 20.6556H28.7224V15.2778C28.7224 13.7989 29.9324 13.9334 31.4113 13.9334H34.1002V7.21115H28.7224C26.4906 7.21115 24.4739 8.12537 23.0219 9.57737C21.5699 11.0294 20.6557 13.046 20.6557 15.2778V20.6556H15.2779V27.3778H20.6557V42.1667Z" stroke="#00CC66" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                      </a>
                    )}
                  </div>
                )}
                <Button 
                  newStyles={styles.askQuestionButton} 
                  onClickFunc={handleAskQuestion}
                >
                  Задать вопрос
                </Button>
              </div>
            ) : null}
            <div className={styles.map}>
              <div className={styles.map__wrapper}>
                <MapDirection/>
              </div>
            </div>
          </div>
        </section>
      </main>
      {isOpen && status === "Success" ? (
        <Modal
          title={ModalTexts.feedbackSuccess.TITLE}
          subTitle={ModalTexts.feedbackSuccess.SUB_TITLE}
          isOpen={true}
          setIsOpen={setIsOpen}
        >
          <Button newStyles={styles.button} onClickFunc={confirmModal}>
            Ок
          </Button>
        </Modal>
      ) : isOpen && status === "Error" ? (
        <CommonErrorModal isOpen={isOpen} setIsOpen={setIsOpen} />
      ) : isOpen && (!status || status === "") ? (
        <Modal
          title="Задать вопрос"
          subTitle=""
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        >
          <FeedbackForm onSubmitFunc={setIsOpen} />
        </Modal>
      ) : null}
      {isLoginModalOpen ? (
        <LoginModal isOpen={isLoginModalOpen} setIsOpen={setIsLoginModalOpen} />
      ) : null}
    </>
  );
};
